import { Component, createRef } from 'react';
import NesGraphics from "../components/services/NesGraphics";
import GameData from "../mobx/GameData";
import { observe, reaction } from 'mobx';

import "./LevelBuilderPage.scss";
import { Accordion, Button, Container, Modal, Row, Col, Dropdown, Card } from "react-bootstrap";
import ApplicationState from "../mobx/ApplicationState";
import { EmbeddedEmulator } from "../components/EmbeddedEmulator";
import GameplayStyle, {getGameplayStyleFromKey} from "../data/GameplayStyle";
import { getCollisionType } from "../data/CollisionType";
import { ChrFile } from "../components/nes/ChrFile";
import LevelReviewDialog from '../components/dialogs/LevelReviewDialog';

export default class LevelBuilderPage extends Component {

    gameDataCleanup = null;
    canvas = createRef();
    canvasContext = null;
    tileCache = [];
    currentLevelData = [];
    palettes = [];
    emulatorRef = createRef();

    emulatorVisibleReaction = null;

    confirmData = {
        resolve: null,
        reject: null,
        promise: null
    }

    constructor(props) {
        super(props);

        this.state = {
            width: 14,
            height: 12,
            currentTile: 0,
            isDragging: false,
            currentLevel: 0,
            lastLevel: 64,
            isLoading: true,
            emulatorVisible: false,
            settingStartSpot: false,
            levelReviewDialogVisible: false,

            awaitingConfirm: false,
            awaitingConfirmAction: 'Do it',

            forceRefresh: null,
            emulatorRefresh: 0
        }
    }

    async componentDidMount() {
        this.gameDataCleanup = observe(GameData, () => { 
            this.forceUpdate()
        });

        this.emulatorVisibleReaction = reaction(
            () => ApplicationState.emulatorIsVisible,
            ((isVisibleNow, wasVisibleBefore) => {
                if (isVisibleNow !== wasVisibleBefore) {
                    this.forceUpdate();
                    setTimeout(() => {
                        if (wasVisibleBefore) {
                            this.restoreCanvas();
                        }
                    }, 100);
                }
            })
        );

        this.loadLevel();

        this.clearDragging = this.clearDragging.bind(this)
        document.addEventListener('mouseup', this.clearDragging);
        document.addEventListener('touchend', this.clearDragging);

        this.touchMove = this.touchMove.bind(this);
        document.addEventListener('touchmove', this.touchMove, {passive: false});

        this.canvasContext = this.canvas.current.getContext('2d');
        this.canvasContext.imageSmoothingEnabled = false;

        this.palettes = [
            GameData.currentGame.gamePaletteData.slice(0, 4),
            GameData.currentGame.gamePaletteData.slice(4, 8),
            GameData.currentGame.gamePaletteData.slice(8, 12),
            GameData.currentGame.gamePaletteData.slice(12, 16)                
        ];

        this.state.lastLevel = GameData.currentGame.lastLevel;

        // Fill cache with the tiles we need to use.
        for (let i = 0; i < 16; ++i) {
            this.tileCache.push(await NesGraphics.convertMetaToBase64Image(
                GameData.currentGame.gameGraphicsData, 
                this.palettes[GameData.currentGame.tileData[i].palette],
                i
            ));
        }
        setTimeout(() => this.generateImage(), 100);
        this.setState({isLoading: false});

    }

    restoreCanvas() {
        this.canvasContext = this.canvas.current.getContext('2d');
        this.canvasContext.imageSmoothingEnabled = false;
        this.generateImage();
    }

    componentWillUnmount() {
        if (this.gameDataCleanup) {
            this.gameDataCleanup();
        }
        if (this.emulatorVisibleReaction) {
            this.emulatorVisibleReaction();
        }
        document.removeEventListener('mouseup', this.clearDragging);
        document.removeEventListener('touchend', this.clearDragging);
        document.removeEventListener('touchmove', this.touchMove, {passive: false});
    }

    clearDragging() {
        this.setState({isDragging: false});
    }

    componentDidUpdate() {
    }

    getSelectedTileX() {
        return (this.state.currentTile % 8) * 16;
    }

    getSelectedTileY() {
        return Math.floor(this.state.currentTile / 8) * 16;
    }

    skipLevel() {
        this.emulatorRef.current.skipLevel();
    }    

    loadLevel() {
        this.currentLevelData = JSON.parse(JSON.stringify(GameData.currentGame.levels[this.state.currentLevel]));
        ApplicationState.unsetCurrentPageUnsaved();
    }

    async setLevel(id) {
        if (ApplicationState.currentPageHasUnsavedChanges) {
            try { 
                await this.showConfirm('Switch levels without saving');
            } catch (e) { /* user cancelled, ignore and bail. */ return; }
        }
        await new Promise(resolve => this.setState({currentLevel: id}, resolve));
        this.loadLevel();
        await this.generateImage();
        // There's some sorta bug here with loading some of the settings on a screen. 
        // I should probably figure out why, but for now just force it.
        this.forceUpdate();
    }

    async setLastLevel(id) {
        this.setState({
            lastLevel: id
        });

        GameData.replaceCurrentGame({
            ...GameData.currentGame,
            lastLevel: id
        });

        ApplicationState.addAlert('success', 'Game updated to end after ' + (id+1) + ' levels!');
    }

    async drawTile(tileId, tileIndex) {
        const x = (tileIndex % 14);
        const y = Math.floor(tileIndex / 14);
        const imgToCopy = document.getElementById('tile-picker-tile-' + tileId);
        this.canvasContext.drawImage(imgToCopy, x*32, y*32, 32, 32);
        return;
    }

    async generateImage() {
        if (!GameData.currentGame) { return; }

        for (let i = 0; i < 14; ++i) {
            this.drawTile(this.currentLevelData.borderTile, i);
            this.drawTile(this.currentLevelData.borderTile, (11 * 14) + i);

            if (i < 12) {
                this.drawTile(this.currentLevelData.borderTile, i*14);
                this.drawTile(this.currentLevelData.borderTile, i*14 + 13);    
            }
        }

        for (let x = 0; x < 12; x++) {
            for (let y = 0; y < 10; y++) {
                this.drawTile(this.currentLevelData.tiles[y*12+x], (x+1) + (y+1)*14)
            }
        }
    }

    touchMove(event) {
        this.doTileUpdate(event, true);
    }
    
    async doTileUpdate(event, cancelEvent = false) {
        const bounds = event.target.getBoundingClientRect(), 
            x = (event.clientX || event.touches[0].clientX) - bounds.left, 
            y = (event.clientY || event.touches[0].clientY) - bounds.top;
        if (!this.state.isDragging) {
            return;
        }
        if (x < 448 && y < (384 - 16)) {
            if (cancelEvent) { event.preventDefault(); }
            const tileX = Math.floor(x / 32) - 1, tileY = Math.floor(y / 32) - 1;

            if (this.state.settingStartSpot) {
                this.currentLevelData.startPosition = ((tileX) & 0x0f) + ((tileY) << 4);
                this.forceUpdate();
                return;
            }

            if (event.buttons === 0x02) {
                // Rightclick - copy the tile instead.
                const tile = this.currentLevelData.tiles[tileX + (tileY * 12)];
                if (typeof tile === 'undefined') { return; }
                this.setState({
                    currentTile: tile
                });
                return;
            }

            // Can't edit border, sorry!
            if (tileX === -1 || tileX === 12 || tileY === -1 || tileY === 10) {
                return;
            }

            if (this.currentLevelData.tiles[tileX + (tileY * 12)] !== this.state.currentTile) {
                this.drawTile(this.state.currentTile, tileX + 1 + ((tileY + 1) * 14));
                this.currentLevelData.tiles[tileX + (tileY * 12)] = this.state.currentTile;
                ApplicationState.setCurrentPageUnsaved();
            }

        }
    }

    getTileType(tileId) {
        return getCollisionType(GameData.currentGame.tileData[tileId].collision);
    }

    updateBorder() {
        this.currentLevelData.borderTile = this.state.currentTile;
        this.generateImage();
        ApplicationState.setCurrentPageUnsaved();
        this.setState({forceRefresh: Math.random()})
    }

    saveData() {
        let levels = Array.from(GameData.currentGame.levels);
        levels[this.state.currentLevel] = this.currentLevelData;
        GameData.replaceCurrentGame({
            ...GameData.currentGame,
            levels: levels
        });
        ApplicationState.unsetCurrentPageUnsaved();
        ApplicationState.addAlert('success', 'Level saved successfully!');
    }

    testCurrentLevel() {
        this.setState({emulatorVisible: true});
    }

    showConfirm(action) {
        this.confirmData.promise = new Promise((resolve, reject) => {
            this.confirmData.resolve = resolve;
            this.confirmData.reject = reject;
        });
        this.setState({
            awaitingConfirm: true,
            awaitingConfirmAction: action
        });

        return this.confirmData.promise;
    }

    cancelConfirm() {
        this.confirmData.reject();
        this.confirmData = {
            promise: null,
            resolve: null, 
            reject: null
        };
        this.setState({awaitingConfirm: false})
    }

    continueConfirm() {
        this.confirmData.resolve();
        this.confirmData = {
            promise: null,
            resolve: null, 
            reject: null
        };
        this.setState({awaitingConfirm: false})
    }

    resetEmulator() {
        this.setState({emulatorRefresh: this.state.emulatorRefresh + 1})
    }

    async showLevelReviewDialog() {
        if (ApplicationState.currentPageHasUnsavedChanges) {
            try { 
                await this.showConfirm('Switch levels without saving');
            } catch (e) { /* user cancelled, ignore and bail. */ return; }
        }
        ApplicationState.unsetCurrentPageUnsaved();
        this.setLevel(0);
        this.setState({levelReviewDialogVisible: true});
    }

    hideLevelReviewDialog() {
        this.setState({levelReviewDialogVisible: false}, () => this.setLevel(0));
    }
    
    render() {
        let mainPage = (<></>);
        if (ApplicationState.emulatorIsVisible) {
            return <></>
        }
        if (!this.state.emulatorVisible) {
            mainPage = (
                <>
                    {this.state.levelReviewDialogVisible ? 
                        <LevelReviewDialog onClose={() => this.hideLevelReviewDialog()} /> : 
                    <></>}
                    <Container>
                        <Row>
                            <Col>
                                <h1>Level Designer</h1>
                                <p>Edit the levels in your game below. You can also set the total number of levels below, which will determine when the game ends.</p>
                            </Col>
                        </Row>
                        <Row>
                            <Col className="centered-row">
                                <div className="level-selector">
                                    <Button variant="primary" size="sm" className="" disabled={this.state.currentLevel < 1} onClick={() => this.setLevel(this.state.currentLevel-1)}>&lt; Prev</Button>
                                    <div className="level-dd">
                                        <span>Level: {(this.state.currentLevel+1).toString().padStart(2, '0')}</span>
                                        <Dropdown className="level-dropdown">
                                            <Dropdown.Toggle variant="outline-primary" size="sm" id="dd-level-selector">Choose Level</Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                {[...Array(64).keys()].map(k => (
                                                    <Dropdown.Item onClick={() => this.setLevel(k)} className={this.state.currentLevel === k ? 'active' : ''} key={'tile-level-'+k}>{k+1}</Dropdown.Item>
                                                ))}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>
                                    <Button variant="primary" size="sm" className="" disabled={this.state.currentLevel > 62} onClick={() => this.setLevel(this.state.currentLevel+1)}>Next &gt;</Button>
                                </div>
                            </Col>
                            <Col>&nbsp;</Col>
                        </Row>
                        <Row>
                            <Col className="centered-row">
                                <div className="canvas-holder">
                                    <canvas className="editor-nametable" 
                                        width="448" 
                                        height="384" 
                                        onMouseDown={event => { this.setState({isDragging: true}, () => this.doTileUpdate(event)); }}
                                        onTouchStart={event => { this.setState({isDragging: true}, () => this.doTileUpdate(event)); }}
                                        onMouseMove={event => this.doTileUpdate(event) } 
                                        onMouseUp={() => this.clearDragging()} 
                                        onTouchEnd={() => this.clearDragging()}
                                        draggable={false} 
                                        ref={this.canvas} 
                                        onContextMenu={event => event.preventDefault()} 
                                    />
                                    <div className="start-position" style={{top: (Math.floor(this.currentLevelData.startPosition >> 4)*32 + 32) + 'px', left: (((this.currentLevelData.startPosition & 0x0f)*32) + 32) + 'px'}}>
                                        <ChrFile data={GameData?.currentGame?.gameGraphicsData} palette={GameData.currentGame.spritePalette} tileId={16} isMeta={true} />
                                    </div>
                                </div>
                            </Col>
                            <Col>
                                <div className="side-panel">
                                    <h6>Tile selection</h6>
                                    <div className="chr-holder">
                                        {this.tileCache.map((tile, idx) => {
                                            return (<img className={"chr-tile " + (idx === this.state.currentTile && !this.state.settingStartSpot ? 'active' : '')} alt="Map Tile" onClick={() => this.setState({currentTile: idx, settingStartSpot: false})} id={"tile-picker-tile-" + idx} key={"tile-picker-" + idx} src={this.tileCache[idx]} />)
                                        })}
                                    </div>
                                    <p className="centered-row mb-1"><small className="actually-small">You can edit these tiles on the "Map Tiles" page.</small></p>

                                    <div className="centered-row mb-2">
                                        <Button size="sm" variant={this.state.settingStartSpot ? "primary" : "outline-primary"} onClick={() => this.setState({settingStartSpot: !this.state.settingStartSpot})}>Choose start position</Button>
                                    </div>

                                    <Card>
                                        <Card.Body>Type: {this.state.settingStartSpot ? 'Start Position': this.getTileType(this.state.currentTile)}</Card.Body>
                                    </Card>

                                    <div className="mt-2 border-tile-row">
                                        <p>
                                            <span>Border Tile:</span>
                                            <img data-force-refresh={this.state.forceRefresh} className="chr-tile" alt="Border Tile" src={this.tileCache[this.currentLevelData.borderTile]} />
                                            <Button size="sm" variant="outline-primary" onClick={() => this.updateBorder() }>Set to selected tile</Button>
                                        </p>
                                    </div>

                                    <div className="centered-row">
                                        <span>Game Style:</span>
                                        <Dropdown className="gamestyle-dropdown">
                                            <Dropdown.Toggle variant="outline-primary" size="sm">{getGameplayStyleFromKey(this.currentLevelData.style)?.toLowerCase()?.replace(/_/g, ' ') || 'Game Default'}</Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item onClick={() => {this.currentLevelData.style = null; ApplicationState.setCurrentPageUnsaved(); this.forceUpdate(); }}>Game Default</Dropdown.Item>
                                                {Object.keys(GameplayStyle).map((style, idx) => {
                                                    return <Dropdown.Item key={"gameplay-style-" + style} onClick={() => { this.currentLevelData.style = idx; ApplicationState.setCurrentPageUnsaved(); this.forceUpdate(); }}>{style.toLowerCase().replace(/_/g, ' ')}</Dropdown.Item>
                                                })}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>

                                    <div className="centered-row mt-3">
                                        <Button variant="primary" onClick={() => this.testCurrentLevel() }>Test Level</Button>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col className="centered-row">
                                <span>Last level: {this.state.lastLevel+1}</span>
                                <Dropdown className="level-dropdown mx-2">
                                    <Dropdown.Toggle variant="outline-primary" size="sm" id="dd-last-level-selector">Choose Level</Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        {[...Array(64).keys()].map(k => (
                                            <Dropdown.Item onClick={() => this.setLastLevel(k)} className={this.state.lastLevel === k ? 'active' : ''} key={'tile-last-level-'+k}>{k+1}</Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                                <Button size="sm" variant="outline-primary" onClick={() => this.showLevelReviewDialog()}>Review/Reorder Levels</Button>
                            </Col>
                            <Col className="button-footer-bar">
                                <Button variant="primary" onClick={() => this.saveData() }>Save Changes</Button>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <div className="mt-5">
                                    <h2>Editor FAQs</h2>
                                    <Accordion flush>
                                    <Accordion.Item eventKey="0">
                                            <Accordion.Header>Why can't I edit the tiles on the outside of the level?</Accordion.Header>
                                            <Accordion.Body>
                                                <p>
                                                    This editor has limits to how much data it can fit on the rom, for a variety of reasons. As such, levels are limited to being
                                                    12 tiles wide by 8 tiles high. To make things look better and give you a little control, a one tile border was added around
                                                    the outside of the level, which only requires us to store that one tile. The border will always be solid, even if you use a
                                                    tile that can be walked through.
                                                </p>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                        <Accordion.Item eventKey="1">
                                            <Accordion.Header>What happens when I set the last level?</Accordion.Header>
                                            <Accordion.Body>
                                                <p>
                                                    This editor can store up to 64 levels for your game. If you are using less, you can set your game to end after less
                                                    levels than that. If you do this, all 64 levels will be saved, and you can continue to edit them, however users playing your
                                                    game will see the credits screen once they beat the level you set.
                                                </p>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                        <Accordion.Item eventKey="2">
                                            <Accordion.Header>Why does my game end after 8 levels? Can I make it longer?</Accordion.Header>
                                            <Accordion.Body>
                                                <p>
                                                    You sure can! The game can have up to 64 levels. Below the level on this screen, there is a setting called
                                                    "Last Level" - change this to pick where your game ends. You can change this to 64 if you want all of the levels
                                                    available in this editor to be playable.
                                                </p>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    </Accordion>
                                </div>

                            </Col>
                        </Row>
                    </Container>
                </>
            )
        }
        return (
            <div className="level-editor">
                <Modal backdrop="static" show={this.state.awaitingConfirm} onClose={() => this.cancelConfirm()} aria-labelledby="Save Changes">
                    <Modal.Header closeButton>
                        <Modal.Title>Unsaved changes</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>You have unsaved changes on the current level. If you do not save, you will lose them. Do you want to continue?</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => this.cancelConfirm()}>Cancel</Button>
                        <Button variant="danger" onClick={() => this.continueConfirm()}>{this.state.awaitingConfirmAction}</Button>
                    </Modal.Footer>
                </Modal>
                <Modal className="emulator-modal" size="lg" fullscreen={"lg-down"} backdrop="static" show={this.state.emulatorVisible} onHide={() => { this.setState({ 'emulatorVisible': false }, () => this.restoreCanvas()); }} aria-labelledby="Emulator Dialog">
                    <Modal.Header closeButton>
                        <Modal.Title>{GameData.currentGame?.title} Level {this.state.currentLevel+1}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <EmbeddedEmulator ref={this.emulatorRef} key={this.state.emulatorRefresh} forceLevel={this.state.currentLevel} forceLevelData={this.currentLevelData} />
                        <Button variant="outline-secondary mx-2" onClick={() => this.skipLevel() }>Skip Level</Button>
                        <Button variant="outline-danger" onClick={() => this.resetEmulator()}>Reset Emulator</Button>
                    </Modal.Body>
                </Modal>

                {mainPage}
            </div>
        );
    }
}